import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAcceptTerms, useSection, useSendResponsible, useToast, useTracker } from '@/hooks';
import { SendResponsiblePayload } from '@/services';
import { boolean, object, string } from '@/utils/validations';

import { TermsFormValues } from './types';

export const useTermsFormRules = () => {
  const { setSection } = useSection();
  const tracker = useTracker();
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalTermsAgreement, setModalTermsAgreement] = useState(false);
  const toast = useToast();
  const { acceptTerms, loading: loadingTermsAcceptance } = useAcceptTerms();
  const { sendResponsible, loading: loadingResponsibleSending } = useSendResponsible();

  useEffect(() => {
    tracker.dispatchWithUserData('risk_onboarding_form_viewed');
  }, []);

  const initialValues = {
    name: '',
    phone: '',
    responsible: 'yes',
    termsAgreement: false,
  };

  const validationSchema = object({
    name: string()
      .name()
      .when('responsible', {
        is: 'no',
        then: (schema) => schema.required(),
      }),
    phone: string()
      .telephoneOrCellphone()
      .when('responsible', {
        is: 'no',
        then: (schema) => schema.required(),
      }),
    responsible: string().required(),
    termsAgreement: boolean().when('responsible', {
      is: 'yes',
      then: (schema) => schema.oneOf([true], ''),
    }),
  });

  const releaseAccess = async () => {
    await acceptTerms();
    toast.show({
      type: 'success',
      title: 'Acesso liberado com sucesso',
    });
    navigate('/gestao-de-risco/registrar');
  };

  const forwardResponsible = async (data: SendResponsiblePayload) => {
    await sendResponsible(data);
    toast.show({
      type: 'success',
      title: 'Recebemos os dados com sucesso!',
      description: 'Em breve entraremos em contato',
    });
    setSection('home');
  };

  const handleSubmit = async ({ responsible, name, phone }: TermsFormValues) => {
    if (responsible === 'no') {
      await forwardResponsible({ name, phone });
      tracker.dispatchWithUserData('risk_onboarding_form_contact_sent');
    } else {
      await releaseAccess();
      tracker.dispatchWithUserData('risk_onboarding_form_accepted');
    }
  };

  const handleModalClose = () => {
    tracker.dispatchWithUserData('risk_onboarding_form_terms_modal_closed');
    setModalOpen(false);
  };

  const handleSelectNotResponsible = () => tracker.dispatchWithUserData('risk_onboarding_form_contact_selected');

  const handleClickTermsAgreement = () => tracker.dispatchWithUserData('risk_onboarding_form_terms_checked');

  const handleClickModalCheck = () => {
    handleClickTermsAgreement();
    setModalTermsAgreement((prev) => !prev);
  };
  const handleClickModalReleaseAccess = async () => {
    await releaseAccess();
    tracker.dispatchWithUserData('risk_onboarding_form_accepted');
    setModalOpen(false);
  };
  const handleClickTermsLink = () => {
    tracker.dispatchWithUserData('risk_onboarding_form_terms_clicked');
    setModalOpen(true);
  };
  const handleClickBack = () => {
    tracker.dispatchWithUserData('risk_onboarding_form_closed');
    setSection('home');
  };

  return {
    handleSubmit,
    handleModalClose,
    handleSelectNotResponsible,
    handleClickModalCheck,
    handleClickModalReleaseAccess,
    handleClickTermsLink,
    handleClickBack,
    handleClickTermsAgreement,
    validationSchema,
    initialValues,
    loadingResponsibleSending,
    loadingTermsAcceptance,
    modalTermsAgreement,
    isModalOpen,
  };
};
